import type { GlobalRuntimeSettings } from '../../services/globalSettings.service';

export type CodeAgentFeatureFlagField =
  | 'codeAgentNativeToolMessages'
  | 'codeAgentPromptCache'
  | 'codeAgentWebTools'
  | 'codeAgentDeferredTools'
  | 'codeAgentPluginSuggestions'
  | 'codeAgentPlanVerifyGate'
  | 'codeAgentSubAgent'
  | 'codeAgentBatchTurnBilling'
  | 'codeAgentClarificationGate'
  | 'codeAgentApiGroundingGuard'
  | 'codeAgentToolOutputSpill'
  | 'codeAgentCompactClearToolOutput'
  | 'codeAgentParallelReadonlyTools'
  | 'codeAgentBackgroundCommands'
  | 'codeAgentUntrustedContentGuard'
  | 'codeAgentEditDiagnostics'
  | 'codeAgentPlanMode'
  | 'codeAgentExploreTool'
  | 'codeAgentMediaRead'
  | 'codeAgentBrowserTools';

export type BooleanSettingField =
  | 'conversationSummaryEnabled'
  | 'adminAgentEnabled'
  | CodeAgentFeatureFlagField;

export const codeAgentFeatureFlagFields: {
  field: CodeAgentFeatureFlagField;
  label: string;
  helperText: string;
}[] = [
  {
    field: 'codeAgentNativeToolMessages',
    label: 'Native tool messages',
    helperText:
      'Store tool calls as native tool_calls/tool_call_id history instead of [Action: …] text. Existing sessions switch at their next turn.',
  },
  {
    field: 'codeAgentPromptCache',
    label: 'Prompt cache',
    helperText:
      'Stable prompt prefix, dynamic block at the end, provider cache breakpoints and window-scaled prompt budget.',
  },
  {
    field: 'codeAgentWebTools',
    label: 'Web tools',
    helperText:
      'Expose web_search / web_fetch server tools to the coding agent (SystemTool rows must be active too).',
  },
  {
    field: 'codeAgentDeferredTools',
    label: 'Deferred tools',
    helperText:
      'Only core tool schemas go to the model; MCP/plugin tools are listed by name and loaded via search_tools.',
  },
  {
    field: 'codeAgentPluginSuggestions',
    label: 'Plugin suggestions',
    helperText: 'Send plugin_suggestion events to the CLI based on workspace facts.',
  },
  {
    field: 'codeAgentPlanVerifyGate',
    label: 'Plan & verify gate',
    helperText:
      'Plan-first nudge for broad tasks, per-task sub-turn ledger, require a passing verification command before a task can be completed, retry on open test failures, and block ending the turn while planned tasks are unfinished.',
  },
  {
    field: 'codeAgentSubAgent',
    label: 'Sub-agent',
    helperText:
      'Run todo tasks flagged run_as_sub_agent in a scoped nested agent (sequential, cheaper model tier when configured). Experimental.',
  },
  {
    field: 'codeAgentBatchTurnBilling',
    label: 'Batch turn billing',
    helperText:
      'Settle quota once when the turn ends instead of after every model call. The quota hold also covers unsettled turn usage.',
  },
  {
    field: 'codeAgentClarificationGate',
    label: 'Clarification gate (default on)',
    helperText:
      'Reject ask_user_clarification calls with fewer than 2 options or before 4 discovery tool calls (one nudge per turn).',
  },
  {
    field: 'codeAgentApiGroundingGuard',
    label: 'API grounding guard (default on)',
    helperText:
      'Block frontend edits that call an API route with no evidence in the workspace; the agent must search the server first (one nudge per turn).',
  },
  {
    field: 'codeAgentToolOutputSpill',
    label: 'Tool output spill',
    helperText:
      'Save tool output over 24 KB to a CLI-side file and send the model a preview plus the file path instead of cutting the output. Files live under ~/.appai/sessions and are removed after 30 days or by appai purge.',
  },
  {
    field: 'codeAgentCompactClearToolOutput',
    label: 'Compact: clear old tool output',
    helperText:
      'Before summarizing, clear old tool results (keeping the 3 most recent) and stop with an error if compaction thrashes. Enables /compact [focus].',
  },
  {
    field: 'codeAgentParallelReadonlyTools',
    label: 'Parallel read-only tools',
    helperText:
      'The CLI runs up to 4 consecutive read-only tool calls in parallel; results are returned in the original order.',
  },
  {
    field: 'codeAgentBackgroundCommands',
    label: 'Background commands',
    helperText:
      'Expose run_in_background and task_stop; commands that time out move to the background instead of being killed (max 5 tasks per hour).',
  },
  {
    field: 'codeAgentUntrustedContentGuard',
    label: 'Untrusted content guard',
    helperText:
      'Wrap web/MCP/plugin/read_media output in <untrusted_content> and require manual approval for writes and shell commands later in the same turn (Always and headless auto-approve are ignored).',
  },
  {
    field: 'codeAgentEditDiagnostics',
    label: 'Edit diagnostics',
    helperText:
      'After editing a .ts file, the CLI runs the project tsc and appends up to 20 type errors for that file to the tool result.',
  },
  {
    field: 'codeAgentPlanMode',
    label: 'Plan mode',
    helperText:
      'Enable /plan: the server blocks mutating tools until the user approves a plan submitted with submit_plan (needs the submit_plan SystemTool seed).',
  },
  {
    field: 'codeAgentExploreTool',
    label: 'Explore tool',
    helperText:
      'Expose the read-only explore sub-agent tool (depth 3, 12 steps per call). Only a short summary returns to the main context; cost counts toward the parent turn.',
  },
  {
    field: 'codeAgentMediaRead',
    label: 'Media read (images & documents)',
    helperText:
      'Expose read_media and accept attached images/PDF/docx/xlsx/pptx; the server turns them into text with a vision model or text extraction (charged to the turn). Output is treated as untrusted content.',
  },
  {
    field: 'codeAgentBrowserTools',
    label: 'Browser debug tools',
    helperText:
      'Expose browser_* tools (needs the AppAI Chrome extension + appai browser pair on the user machine and the browser tools seed). Page content is untrusted; limits and blocked origins are set in Code Agent Kill Switches.',
  },
];

export type NumberSettingField = Exclude<
  keyof GlobalRuntimeSettings,
  | BooleanSettingField
  | 'chatStreamMaxToolIterationsByPackage'
  | 'adminAgentExtraDenyPatterns'
  | 'generatedImageAllowedHosts'
>;

export const numberFieldLabels: Record<NumberSettingField, { label: string; helperText: string }> =
  {
    llmGatewayAiBindingTimeoutMs: {
      label: 'AI binding stream timeout',
      helperText: 'Milliseconds for env.AI.run to hand back a stream before falling back to REST.',
    },
    llmGatewayAiBindingRunTimeoutMs: {
      label: 'AI binding run timeout',
      helperText: 'Milliseconds allowed for a whole non-stream env.AI.run generation.',
    },
    llmGatewayAiBindingFailureCooldownMs: {
      label: 'AI binding failure cooldown',
      helperText: 'Milliseconds to skip the same model/gateway AI binding after a binding failure.',
    },
    llmGatewayLogFetchTimeoutMs: {
      label: 'AI Gateway log fetch timeout',
      helperText: 'Milliseconds to wait for the AI Gateway log (cost/usage) after a binding call.',
    },
    chatStreamSummaryAfterMessages: {
      label: 'Chat summary threshold',
      helperText:
        'Completed chat messages after the latest rolling summary before creating a new one.',
    },
    chatStreamSummaryTokenGuard: {
      label: 'Chat summary token guard',
      helperText: 'Generate a chat rolling summary when unsummarized tokens reach this guard.',
    },
    codeAgentSummaryAfterMessages: {
      label: 'Code Agent summary threshold',
      helperText: 'Agent messages after the latest rolling summary before creating a new one.',
    },
    codeAgentSummaryTokenGuard: {
      label: 'Code Agent summary token guard',
      helperText:
        'Generate a Code Agent rolling summary when unsummarized tokens reach this guard.',
    },
    codeAgentSummaryTurnGuard: {
      label: 'Code Agent summary turn guard',
      helperText: 'Generate a Code Agent rolling summary after this many unsummarized turns.',
    },
    codeAgentSummaryToolIterationGuard: {
      label: 'Code Agent summary tool-iteration guard',
      helperText:
        'Generate a Code Agent rolling summary after this many unsummarized tool iterations.',
    },
    codeAgentFirstChunkTimeoutMs: {
      label: 'Code Agent first-chunk timeout',
      helperText:
        'Milliseconds to wait for the first model delta (text or reasoning) before aborting. Raise for reasoning models; LLMModel config firstChunkTimeoutMs overrides per model.',
    },
    codeAgentStreamStallTimeoutMs: {
      label: 'Code Agent stream stall timeout',
      helperText:
        'Milliseconds of silence between model deltas before the stream is treated as stalled. LLMModel config streamStallTimeoutMs overrides per model.',
    },
    chatStreamMaxToolIterations: {
      label: 'Chat tool-loop cap (global)',
      helperText:
        'Max tool-call iterations per chat turn before the gateway stops. Per-package values below override it.',
    },
    chatRuntimeSessionContextTtlSeconds: {
      label: 'Session context / stream runtime TTL (s)',
      helperText:
        'Seconds the chat session context and history snapshot stay cached. Safe to raise: the DO refreshes them after every turn.',
    },
    chatRuntimeQuotaSnapshotTtlSeconds: {
      label: 'Quota snapshot TTL (s)',
      helperText:
        'Seconds the per-user quota snapshot stays cached; refreshed after usage settlement.',
    },
    chatRuntimeAgentConfigTtlSeconds: {
      label: 'Agent config TTL (s)',
      helperText:
        'Seconds the compiled agent config stays cached. Overrides AGENT_CONFIG_CACHE_TTL_MINUTES.',
    },
    quotaLowThresholdPercent: {
      label: 'Quota low threshold (%)',
      helperText: 'Percentage used at which user-facing monthly allowance status becomes Low.',
    },
    billingCreditsPerUsd: {
      label: 'Credits per USD',
      helperText: 'Credits per catalog USD; positive integer.',
    },
    billingCostVarianceThresholdPercent: {
      label: 'Cost variance threshold (%)',
      helperText: 'Flag variance at or above this percentage; integer from 0 to 100.',
    },
    billingCostVarianceMinAbsUsd: {
      label: 'Minimum cost variance (USD)',
      helperText: 'Minimum absolute difference per call; zero or greater.',
    },
    billingFallbackImageUsd: {
      label: 'Fallback image cost (USD)',
      helperText: 'Estimate per image without catalog pricing; zero or greater.',
    },
    billingOverheadFactor: {
      label: 'Overhead factor',
      helperText: 'Multiplier applied to catalog cost; at least 1.',
    },
    billingTargetMargin: {
      label: 'Target margin',
      helperText: 'Fractional margin target; zero or greater.',
    },
    billingPaymentFeeRate: {
      label: 'Payment fee rate',
      helperText: 'Fraction from 0 inclusive to 1 exclusive.',
    },
    billingTaxRate: {
      label: 'Tax rate',
      helperText: 'Fraction from 0 inclusive to 1 exclusive.',
    },
    billingFxBuffer: {
      label: 'FX buffer',
      helperText: 'Fractional currency buffer; zero or greater.',
    },
    billingMinUtilization: {
      label: 'Minimum utilization',
      helperText: 'Fraction greater than 0 and at most 1.',
    },
    billingReportMaxDays: {
      label: 'Report maximum days',
      helperText: 'Maximum UTC day count; positive integer.',
    },
    billingUnitCostWindowDays: {
      label: 'Unit cost window (days)',
      helperText: 'Lookback window; positive integer.',
    },
    adminAgentMaxRequestsPerMinute: {
      label: 'Max chat turns per minute',
      helperText: 'Per admin. Stops runaway loops or scripts hammering the assistant.',
    },
    adminAgentMaxDailyTokens: {
      label: 'Max tokens per day',
      helperText:
        'Per admin, UTC day. The platform pays for this usage, so this is the spend cap; the assistant stops answering until the next day once it is reached.',
    },
    adminAgentMaxPendingActions: {
      label: 'Max pending approvals',
      helperText: 'Change requests one admin may have waiting for approval at the same time.',
    },
    adminAgentActionTtlSeconds: {
      label: 'Approval expires after (s)',
      helperText: 'Seconds before a pending change request expires if nobody approves it.',
    },
    adminAgentMaxBodyBytes: {
      label: 'Max change body (bytes)',
      helperText: 'Largest request body the assistant may propose for a change.',
    },
    adminAgentReadTokenTtlSeconds: {
      label: 'Read token lifetime (s)',
      helperText: 'Lifetime of the short-lived token used for the assistant’s read calls.',
    },
    adminAgentMaxResultChars: {
      label: 'Max result size (chars)',
      helperText: 'API results longer than this are truncated before the model sees them.',
    },
    adminAgentSearchMaxResults: {
      label: 'Max search results',
      helperText: 'Operations returned by one admin_ops_search call.',
    },
    adminAgentSocketTicketTtlSeconds: {
      label: 'Chat socket ticket lifetime (s)',
      helperText:
        'The chat connects with a one-time ticket instead of the login token. It must be used within this many seconds.',
    },
    voiceWsTicketTtlSeconds: {
      label: 'Voice connection ticket lifetime (s)',
      helperText:
        'When a user starts a voice call, the app first asks the server for a one-time ticket and then uses it to open the voice connection, so the login token never appears in the URL. This is how many seconds the app has to connect after receiving the ticket; after that the ticket expires and the call must be started again. A ticket can only be used once. Allowed range 10–300. Default 60. Raise it only if users on slow networks see calls fail to connect; keep it short for security.',
    },
  };

export const numberFields = Object.keys(numberFieldLabels) as NumberSettingField[];
export const codeAgentStreamNumberFields: NumberSettingField[] = [
  'codeAgentFirstChunkTimeoutMs',
  'codeAgentStreamStallTimeoutMs',
];
export const chatToolLoopNumberFields: NumberSettingField[] = ['chatStreamMaxToolIterations'];
export const runtimeTtlNumberFields: NumberSettingField[] = [
  'chatRuntimeSessionContextTtlSeconds',
  'chatRuntimeQuotaSnapshotTtlSeconds',
  'chatRuntimeAgentConfigTtlSeconds',
];
export const quotaNumberFields: NumberSettingField[] = ['quotaLowThresholdPercent'];
export const billingPricingNumberFields: NumberSettingField[] = [
  'billingCreditsPerUsd',
  'billingOverheadFactor',
  'billingTargetMargin',
  'billingPaymentFeeRate',
  'billingTaxRate',
  'billingFxBuffer',
  'billingMinUtilization',
  'billingReportMaxDays',
  'billingUnitCostWindowDays',
  'billingCostVarianceMinAbsUsd',
  'billingCostVarianceThresholdPercent',
  'billingFallbackImageUsd',
];
/** Admin Agent clusters, in the order the settings tab shows them. */
export const adminAgentLimitNumberFields: NumberSettingField[] = [
  'adminAgentMaxRequestsPerMinute',
  'adminAgentMaxDailyTokens',
];
export const adminAgentApprovalNumberFields: NumberSettingField[] = [
  'adminAgentMaxPendingActions',
  'adminAgentActionTtlSeconds',
  'adminAgentMaxBodyBytes',
];
export const adminAgentOutputNumberFields: NumberSettingField[] = [
  'adminAgentReadTokenTtlSeconds',
  'adminAgentMaxResultChars',
  'adminAgentSearchMaxResults',
];
export const adminAgentConnectionNumberFields: NumberSettingField[] = [
  'adminAgentSocketTicketTtlSeconds',
];
/** Realtime voice settings, shown in the AI runtime tab. */
export const voiceNumberFields: NumberSettingField[] = ['voiceWsTicketTtlSeconds'];
/** Mirrors the range aibe enforces for the voice ticket lifetime (seconds). */
export const VOICE_TICKET_TTL_MIN_SECONDS = 10;
export const VOICE_TICKET_TTL_MAX_SECONDS = 300;
const adminAgentNumberFields = [
  ...adminAgentLimitNumberFields,
  ...adminAgentApprovalNumberFields,
  ...adminAgentOutputNumberFields,
  ...adminAgentConnectionNumberFields,
];
export const gatewayNumberFields = numberFields.filter((field) => field.startsWith('llmGateway'));
export const codeAgentSummaryNumberFields = numberFields.filter((field) =>
  field.startsWith('codeAgentSummary')
);
export const summaryNumberFields = numberFields.filter(
  (field) =>
    !field.startsWith('llmGateway') &&
    !field.startsWith('codeAgentSummary') &&
    !codeAgentStreamNumberFields.includes(field) &&
    !chatToolLoopNumberFields.includes(field) &&
    !runtimeTtlNumberFields.includes(field) &&
    !quotaNumberFields.includes(field) &&
    !billingPricingNumberFields.includes(field) &&
    !adminAgentNumberFields.includes(field) &&
    !voiceNumberFields.includes(field)
);
export const fields: (keyof GlobalRuntimeSettings)[] = [
  ...numberFields,
  'conversationSummaryEnabled',
  'adminAgentEnabled',
  ...codeAgentFeatureFlagFields.map((item) => item.field),
  'chatStreamMaxToolIterationsByPackage',
  'adminAgentExtraDenyPatterns',
  'generatedImageAllowedHosts',
];

/** Everything the Code Agent > Runtime Settings page edits (moved out of Global Settings). */
export const codeAgentRuntimeNumberFields: NumberSettingField[] = [
  ...codeAgentSummaryNumberFields,
  ...codeAgentStreamNumberFields,
];
